import authI18n from '~~/i18n/locales/common/auth/shared.json'

import type { Nullable } from '~/types'

type Credentials = {
  username?: string
  password?: string
}

export default function useAuthentication() {
  const { $captureError } = useNuxtApp()
  const { status, token, signIn, signOut, getSession } = useAuth()
  const { user, userProfileState } = useUser()
  const { setToken, clearToken } = useAuthState()
  const localePath = useLocalePath()
  const { routeBaseName } = useBaseName()
  const authenticationProvider = useAuthenticationProvider()
  const authenticationModalShown = useAuthenticationModal()
  const authenticationType = useAuthenticationType()
  const profileCompletionPending = useProfileCompletionPending()
  const authenticationIntent = useAuthenticationIntent()

  const isLoggedIn = computed(() => status.value === 'authenticated')

  /**
   * Logs in the user.
   *
   * @param credentials The credentials to use for authentication
   * @param token The token to use for authentication
   * @param isSignup Whether the user is signing up
   * @param callbackFn The callback function to call after authentication
   * @returns A promise that resolves when the authentication is complete
   */
  async function authenticate({
    credentials,
    token,
    isSignup,
    callbackFn,
  }: {
    credentials?: Credentials
    token?: string
    isSignup?: boolean
    callbackFn?: () => void
  }) {
    if (!credentials && !token) {
      return
    }

    try {
      if (credentials) {
        await signIn(credentials, { redirect: false })
      }
      else if (token) {
        setToken(token)
        await getSession()
      }

      await refreshNuxtData()

      // User needs to complete their profile.
      const pendingRequestToBookUrl = usePendingRequestToBookUrlCookie().value

      if (userProfileState.value !== 'Complete' && (isSignup || pendingRequestToBookUrl)) {
        profileCompletionPending.value = true

        if (pendingRequestToBookUrl) {
          authenticationIntent.value = 'BookingIntent'
        }

        const redirectCookie = useRedirectCookie()

        if (redirectCookie.value) {
          await navigateTo(redirectCookie.value)
        }

        authenticationModalShown.value = true

        return
      }

      hideAuthModal()

      if (callbackFn) {
        callbackFn()
        return
      }

      const isNewOwner = Boolean(user.value?.UserType === 'Owner' && isSignup)

      await redirectAfterAuthentication(isNewOwner)
    }
    catch (err: unknown) {
      $captureError(err)
      /**
       * Throwing the error so we display the right error message in the UI.
       */
      throw err
    }
  }

  async function redirectAfterAuthentication(isNewOwner: boolean) {
    const unauthorizedRedirectCookie = useUnauthorizedRedirectCookie()

    /**
     * Intent cookie is set when we want to trigger an action after the user authenticates with an external provider.
     */
    const intentCookie = useIntentCookie()

    /**
     * Auth type cookie indicates whether the user was on signup or login when starting external auth.
     */
    const authTypeCookie = useAuthTypeCookie()

    /**
     * Redirect URL cookie is set as the return URL after the user authenticates with an external provider.
     */
    const redirectURLCookie = useRedirectCookie()

    // Copy the values to local variables and clear the cookies
    const unauthorizedRedirect = unauthorizedRedirectCookie.value
    unauthorizedRedirectCookie.value = null

    const intent = intentCookie.value
    intentCookie.value = null

    authTypeCookie.value = null

    const redirectURL = redirectURLCookie.value
    redirectURLCookie.value = null

    const pendingRequestToBookUrlCookie = usePendingRequestToBookUrlCookie()
    const pendingRequestToBookUrl = pendingRequestToBookUrlCookie.value

    pendingRequestToBookUrlCookie.value = null

    if (pendingRequestToBookUrl && userProfileState.value === 'Complete') {
      await navigateTo(pendingRequestToBookUrl)

      return
    }

    /**
     * 1. Redirect authenticated user to the requested unauthorized URL.
     * 2. Redirect booking intent to RTB
     * 3. Redirect new owner to the RV listing flow.
     * 4. Redirect owner to the dashboard.
     * 5. Redirect authenticated user to the previous URL before external provider redirection.
     * 6. Redirect authenticated user to the homepage.
     */
    let path = unauthorizedRedirect

    if (!path) {
      const isRouteOwnerPage = routeBaseName.value === 'owner' || redirectURL?.startsWith(localePath('owner'))

      if (intent === 'BookingIntent') {
        path = redirectURL
      }
      else if (isNewOwner || isRouteOwnerPage || intent === 'ListingIntent') {
        path = localePath({ name: 'dashboard-rvs-new', params: { dashboard: 'host' } })
      }
      else if (user.value?.IsOwner) {
        path = localePath({ name: 'dashboard', params: { dashboard: 'host' } })
      }
      else {
        path = redirectURL
      }
    }

    // If path is set, augment url with intent query parameter and navigate.
    if (path) {
      const [pathname, search = ''] = path.split('?')
      const query: Record<string, string | undefined> = Object.fromEntries(new URLSearchParams(search))

      query.intent = intent ?? undefined

      await navigateTo({ path: pathname, query })
    }
  }

  /**
   * Prompt users to fill out their profile.
   */
  async function finishProfile() {
    profileCompletionPending.value = false

    await redirectAfterAuthentication(user.value?.UserType === 'Owner')

    hideAuthModal()
  }

  /**
   * Logs out a user (removes their cookie) and clears their list of RV
   * collections.
   */
  async function logOut(options?: { callbackUrl?: string, external?: boolean }) {
    clearToken()

    // RVezy Identity Service (RIS) provider cookie is set when the user logs in
    // with an External Provider. So we clear it in that case.
    const risProviderCookie = useRisProviderCookie()

    if (risProviderCookie.value) {
      const { externalDeauthenticate } = useExternalAuthProvider()

      // We don't await this as it's slow and non-blocking.
      externalDeauthenticate(risProviderCookie.value)

      risProviderCookie.value = null
    }

    // Reset Braze so the session ends.
    const brazeApi = await useBraze()
    brazeApi.reset()

    const { callbackUrl, external = true } = { ...options }

    const requiresAuthentication = useRoute().matched.some((record) => {
      const middleware = record.meta.middleware
      return Array.isArray(middleware)
        ? middleware.includes('authentication')
        : middleware === 'authentication'
    })

    // Authed routes cannot stay put (auth middleware would send users to sign-in).
    // Explicit callbackUrl always wins (e.g. masquerade).
    const resolvedCallbackUrl = callbackUrl ?? (requiresAuthentication ? localePath('index') : undefined)

    await signOut({ callbackUrl: resolvedCallbackUrl, external })
  }

  /**
   * Watch for the authentication modal to be hidden and clear the unauthorized redirect cookie.
   */
  /* v8 ignore start */
  watch(authenticationModalShown, (isOpen) => {
    if (isOpen) {
      return
    }

    useUnauthorizedRedirectCookie().value = null

    if (isLoggedIn.value) {
      return
    }

    const intentCookie = useIntentCookie()
    const redirectCookie = useRedirectCookie()

    intentCookie.value = null
    redirectCookie.value = null
  })
  /* v8 ignore stop */

  function hideAuthModal() {
    authenticationType.value = null
    authenticationModalShown.value = false
  }

  /**
   * Resolves an auth error code (and optional fallback) to a translated message.
   * Uses auth.errors.{code} if the translation exists, otherwise fallback or auth.errors.other.
   */
  function getAuthErrorMessage(options: {
    code: MaybeRefOrGetter<Nullable<string>>
    fallback?: MaybeRefOrGetter<Nullable<string>>
  }) {
    const { t, te } = useI18n({
      useScope: 'global',
      messages: authI18n,
    })

    return computed(() => {
      const code = toValue(options.code)
      const fallback = options.fallback !== undefined ? toValue(options.fallback) : undefined
      const hasCode = code !== null && code !== undefined && code !== ''

      if (!hasCode) {
        return fallback ?? ''
      }

      if (te(`auth.errors.${code}`)) {
        return t(`auth.errors.${code}`)
      }

      if (fallback) {
        return fallback
      }

      return t('auth.errors.other', { errorCode: code })
    })
  }

  return {
    status,
    token,
    isLoggedIn,
    hideAuthModal,
    authenticationIntent,
    authenticationModalShown,
    authenticationProvider,
    authenticationType,
    authenticate,
    finishProfile,
    logOut,
    getAuthErrorMessage,
  }
}
